import { httpsCallable } from 'firebase/functions';
import { browserSupportsWebAuthn, startAuthentication, startRegistration } from '@simplewebauthn/browser';
import { functions } from '../../config/firebaseConfig';

// Set once face login has been set up on this device (the passkey itself
// lives in the device's own keychain; this only decides what the button does)
const ENROLLED_KEY = 'habcare.passkeyEnrolled';

const call = async (name, data) => (await httpsCallable(functions, name)(data)).data;

/** The device can verify its user itself (Windows Hello, Face ID, fingerprint). */
export const isPasskeyAvailable = async () => {
  try {
    if (!browserSupportsWebAuthn()) return false;
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch (error) {
    return false;
  }
};

export const hasDevicePasskey = () => {
  try {
    return localStorage.getItem(ENROLLED_KEY) === '1';
  } catch (error) {
    return false;
  }
};

export const setDevicePasskey = (enrolled) => {
  try {
    if (enrolled) localStorage.setItem(ENROLLED_KEY, '1');
    else localStorage.removeItem(ENROLLED_KEY);
  } catch (error) {
    // Storage unavailable: the button then offers the set-up again
  }
};

/**
 * Why a passkey step failed, as one of: cancelled (the user closed the
 * device prompt), unknown (this device's face login is no longer on record),
 * inactive, expired, exists, fresh-login, unsupported, failed.
 */
export const passkeyErrorReason = (error) => {
  if (!error) return 'failed';
  if (error.name === 'NotAllowedError' || error.name === 'AbortError' || error.code === 'ERROR_CEREMONY_ABORTED') return 'cancelled';
  if (error.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED') return 'exists';
  if (error.code === 'ERROR_AUTHENTICATOR_MISSING_USER_VERIFICATION_SUPPORT' || error.code === 'ERROR_AUTHENTICATOR_MISSING_DISCOVERABLE_CREDENTIAL_SUPPORT') return 'unsupported';
  const reason = error.details?.reason;
  if (reason === 'unknown-credential' || reason === 'unbound') return 'unknown';
  if (reason === 'inactive' || reason === 'disabled' || reason === 'employee') return 'inactive';
  if (reason === 'challenge') return 'expired';
  if (reason === 'exists') return 'exists';
  if (reason === 'fresh-login') return 'fresh-login';
  return 'failed';
};

/**
 * Face login: the device verifies its user and signs a challenge; the server
 * checks the signature and returns a sign-in token for that account.
 * Resolves to { token, employeeId, email }.
 */
export const loginWithPasskey = async () => {
  const { options, challengeId } = await call('passkeyLoginOptions');
  const response = await startAuthentication({ optionsJSON: options });
  try {
    return await call('passkeyLoginVerify', { challengeId, response });
  } catch (error) {
    if (passkeyErrorReason(error) === 'unknown') setDevicePasskey(false);
    throw error;
  }
};

/** Sets up face login on this device for the user who just signed in with a password. */
export const registerPasskey = async (employeeId) => {
  const { options, challengeId } = await call('passkeyRegisterOptions', { employeeId });
  const response = await startRegistration({ optionsJSON: options });
  await call('passkeyRegisterVerify', { challengeId, response });
  setDevicePasskey(true);
};
